export type RetrySleep = (delayMs: number, signal?: AbortSignal) => Promise<void>;

type AxiosLikeError = {
  isAxiosError?: boolean;
  response?: { status: number };
  code?: string;
};

function isAxiosLikeError(error: unknown): error is AxiosLikeError {
  return typeof error === 'object'
    && error !== null
    && (error as AxiosLikeError).isAxiosError === true;
}

const MAX_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [1000, 3000] as const;
const RETRYABLE_STATUSES = new Set([502, 503, 504]);
const RETRYABLE_TRANSPORT_CODES = new Set([
  'ERR_NETWORK',
  'ECONNABORTED',
  'ETIMEDOUT',
  'ECONNRESET',
]);

function abortReason(signal: AbortSignal): unknown {
  return signal.reason ?? new DOMException('Auth verification canceled', 'AbortError');
}

const sleep: RetrySleep = (delayMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) {
    reject(abortReason(signal));
    return;
  }

  const timer = globalThis.setTimeout(() => {
    signal?.removeEventListener('abort', handleAbort);
    resolve();
  }, delayMs);
  const handleAbort = () => {
    globalThis.clearTimeout(timer);
    reject(abortReason(signal!));
  };
  signal?.addEventListener('abort', handleAbort, { once: true });
});

function isRetryableVerificationError(error: unknown): boolean {
  if (!isAxiosLikeError(error)) return false;

  if (typeof error.response?.status === 'number') {
    return RETRYABLE_STATUSES.has(error.response.status);
  }

  return typeof error.code === 'string' && RETRYABLE_TRANSPORT_CODES.has(error.code);
}

export async function verifyWithRetry<T>(
  request: (signal?: AbortSignal) => Promise<T>,
  wait: RetrySleep = sleep,
  signal?: AbortSignal,
): Promise<T> {
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
    if (signal?.aborted) throw abortReason(signal);

    try {
      return await request(signal);
    } catch (error) {
      const isLastAttempt = attempt === MAX_ATTEMPTS - 1;
      if (isLastAttempt || !isRetryableVerificationError(error)) throw error;

      const delayMs = RETRY_DELAYS_MS[attempt];
      if (delayMs === undefined) throw error;
      await wait(delayMs, signal);
    }
  }

  throw new Error('Auth verification retry loop exhausted unexpectedly');
}
